/**
 * WebAudioFallback — JS-only audio processing pipeline for SIMD-less devices.
 *
 * Builds an OfflineAudioContext node graph from an FFmpeg-style filter chain
 * string, renders it, and encodes the result to WAV (always) or MP3 (when
 * AudioPipeline + lamejs are loaded — also asm.js, no SIMD needed).
 *
 * Replaces ~70% of audio-enhancer's FFmpeg pipeline for users whose CPU
 * lacks WebAssembly SIMD (pre-2008 hardware). Supported FFmpeg ops:
 *
 *   ✓ highpass / lowpass    → BiquadFilterNode (highpass/lowpass type)
 *   ✓ equalizer (peaking)   → BiquadFilterNode (peaking type)
 *   ✓ acompressor           → DynamicsCompressorNode + GainNode (makeup)
 *   ✓ alimiter              → DynamicsCompressorNode at high ratio
 *   ✓ loudnorm (approx)     → DynamicsCompressorNode + GainNode (calibrated)
 *   ✓ stereotools (basic)   → ChannelMerger + GainNode pair (slev)
 *   ✗ afftdn                → SKIPPED (no native FFT denoise)
 *   ✗ agate                 → SKIPPED for v1 (would need AudioWorklet)
 *   ✗ dynaudnorm            → SKIPPED (use loudnorm-class instead)
 *
 * Telemetry surface:
 *   feature_use { action: 'webaudio_fallback_used', slug, opsApplied, opsSkipped }
 *   feature_use { action: 'webaudio_fallback_failed', slug, err }
 *
 * Public API:
 *   await WebAudioFallback.process(file, filterChainString, { ext, slug, onProgress })
 *     → { blob, name, url, size, opsApplied, opsSkipped }
 *
 *   WebAudioFallback.canHandleFormat(ext) → boolean
 *     → 'wav' | 'mp3' supported in v1; other formats need WASM encoder.
 */
(function() {
    'use strict';
    if (window.WebAudioFallback) return;

    var SUPPORTED_FORMATS = ['wav', 'mp3'];

    /**
     * Parse a single FFmpeg filter expression like:
     *   'equalizer=f=2800:t=h:w=1200:g=3.5'
     * into:
     *   { name: 'equalizer', f: 2800, t: 'h', w: 1200, g: 3.5 }
     *
     * Numeric values are coerced to Number; strings like 't=h' stay as strings.
     * dB suffix on threshold values ('threshold=-26dB') is stripped + parsed.
     */
    function parseFilter(expr) {
        var eq = expr.indexOf('=');
        var name = (eq < 0 ? expr : expr.slice(0, eq)).trim();
        var out = { name: name };
        if (eq < 0) return out;
        var rest = expr.slice(eq + 1);
        var pairs = rest.split(':');
        for (var i = 0; i < pairs.length; i++) {
            var p = pairs[i];
            var ki = p.indexOf('=');
            if (ki < 0) {
                // Bare value (e.g. `loudnorm=I=-16:TP=-1.5`'s I value before key)
                continue;
            }
            var k = p.slice(0, ki).trim();
            var v = p.slice(ki + 1).trim();
            // Strip dB / Hz suffix; parse number when possible
            var stripped = v.replace(/(dB|Hz|kHz|ms|s)$/i, '');
            var num = parseFloat(stripped);
            out[k] = (!isNaN(num) && stripped !== '') ? num : v;
        }
        return out;
    }

    /**
     * The Q of an FFmpeg `lowpass` / `highpass`, in the unit Web Audio takes it:
     * for these two filter types BiquadFilterNode reads Q in DECIBELS. Left
     * untouched it is 1 dB — a resonance of +1 dB at the corner where FFmpeg's
     * default (Q 0.707, Butterworth) is −3 dB down, so every such filter in the
     * fallback came out 4 dB hotter at its corner than in the pipeline it stands
     * in for (measured 2026-10-07).
     */
    function ffQdB(f) {
        // FFmpeg: width_type (t) defaults to q, width (w) to 0.707. A width given
        // in hertz or octaves is not translated here — such a filter keeps the default.
        var type = f && (f.t || f.width_type);
        var w = f ? (typeof f.w === 'number' ? f.w : (typeof f.width === 'number' ? f.width : 0)) : 0;
        var q = ((!type || type === 'q') && w > 0) ? w : 0.707;
        return 20 * Math.log10(q);
    }

    /** Split a filter chain `'a=...,b=...,c=...'` into individual filter exprs. */
    function splitChain(chain) {
        // Filters are comma-separated. Args inside a filter are colon-separated.
        // No nested parens to worry about for the FFmpeg ops we support.
        return chain.split(',').map(function(s){ return s.trim(); }).filter(Boolean);
    }

    /**
     * Build a Web Audio node graph from parsed filters. Connects them serially:
     * source → filter1 → filter2 → ... → destination.
     *
     * Returns { firstNode, lastNode, opsApplied[], opsSkipped[] }. Caller
     * connects firstNode to its source and lastNode to ctx.destination.
     */
    function buildGraph(ctx, filters) {
        var nodes = [];
        var opsApplied = [];
        var opsSkipped = [];

        for (var i = 0; i < filters.length; i++) {
            var f = filters[i];
            var node = null;
            switch (f.name) {
                case 'highpass': {
                    node = ctx.createBiquadFilter();
                    node.type = 'highpass';
                    if (typeof f.f === 'number') node.frequency.value = f.f;
                    // FFmpeg `p=2` = 2nd-order = single biquad, Q 0.707 unless
                    // the chain names its own (t=q:w=…).
                    node.Q.value = ffQdB(f);
                    opsApplied.push('highpass@' + (f.f || 0));
                    break;
                }
                case 'lowpass': {
                    node = ctx.createBiquadFilter();
                    node.type = 'lowpass';
                    if (typeof f.f === 'number') node.frequency.value = f.f;
                    node.Q.value = ffQdB(f);
                    opsApplied.push('lowpass@' + (f.f || 0));
                    break;
                }
                case 'equalizer': {
                    node = ctx.createBiquadFilter();
                    node.type = 'peaking';
                    if (typeof f.f === 'number') node.frequency.value = f.f;
                    if (typeof f.g === 'number') node.gain.value = f.g;
                    // FFmpeg `t=h` = Hz-width; Q ≈ f / w (rough)
                    if (typeof f.w === 'number' && typeof f.f === 'number' && f.w > 0) {
                        node.Q.value = f.f / f.w;
                    } else {
                        node.Q.value = 1.0;
                    }
                    opsApplied.push('eq@' + (f.f || 0) + (f.g >= 0 ? '+' : '') + f.g + 'dB');
                    break;
                }
                case 'acompressor': {
                    // Build compressor + makeup gain stage
                    var comp = ctx.createDynamicsCompressor();
                    if (typeof f.threshold === 'number') comp.threshold.value = f.threshold;
                    if (typeof f.ratio === 'number') comp.ratio.value = f.ratio;
                    // FFmpeg attack/release in ms; Web Audio in seconds
                    if (typeof f.attack === 'number') comp.attack.value = f.attack / 1000;
                    if (typeof f.release === 'number') comp.release.value = f.release / 1000;
                    comp.knee.value = 6; // soft knee, matches FFmpeg default
                    var makeup = (typeof f.makeup === 'number') ? f.makeup : 0;
                    if (makeup !== 0) {
                        var makeupGain = ctx.createGain();
                        // FFmpeg makeup is in dB; Web Audio gain is linear.
                        makeupGain.gain.value = Math.pow(10, makeup / 20);
                        comp.connect(makeupGain);
                        // Use a synthetic "node" that exposes connect() as the
                        // makeupGain output — keeps the serial-chain loop simple.
                        node = { _input: comp, _output: makeupGain,
                            connect: function(target){ return makeupGain.connect(target); } };
                    } else {
                        node = comp;
                    }
                    opsApplied.push('comp@' + (f.threshold || -20) + 'dB×' + (f.ratio || 3));
                    break;
                }
                case 'alimiter': {
                    node = ctx.createDynamicsCompressor();
                    // Approximate brick-wall limiter: high ratio + fast attack/release
                    var limitDb = (typeof f.limit === 'number')
                        ? 20 * Math.log10(Math.max(f.limit, 0.001))
                        : -1.0;
                    node.threshold.value = limitDb;
                    node.ratio.value = 20;       // hard limiting
                    node.attack.value = 0.001;   // 1 ms — catches transients
                    node.release.value = 0.05;   // 50 ms
                    node.knee.value = 0;         // hard knee
                    opsApplied.push('limit@' + limitDb.toFixed(1) + 'dB');
                    break;
                }
                case 'loudnorm': {
                    // Approximate streaming-target loudness with a 2-stage chain:
                    // gentle compressor + makeup gain calibrated to the I (LUFS)
                    // target. Real EBU R128 needs a 2-pass measure-then-apply
                    // implementation; we trade exactness for "loud enough on
                    // reasonable inputs".
                    var comp2 = ctx.createDynamicsCompressor();
                    comp2.threshold.value = -18; // catch peaks
                    comp2.ratio.value = 3;
                    comp2.attack.value = 0.005;
                    comp2.release.value = 0.1;
                    comp2.knee.value = 6;
                    var targetLUFS = (typeof f.I === 'number') ? f.I : -16;
                    // Calibrated boost: -16 LUFS target on a typical -23 LUFS
                    // input needs ~+7 dB. We add 5 dB for reasonable inputs.
                    var boostGain = ctx.createGain();
                    boostGain.gain.value = Math.pow(10, 5 / 20);
                    comp2.connect(boostGain);
                    node = { _input: comp2, _output: boostGain,
                        connect: function(target){ return boostGain.connect(target); } };
                    opsApplied.push('loudnorm-approx@I=' + targetLUFS);
                    break;
                }
                case 'stereotools': {
                    // Very basic stereo width via a gain on the side signal.
                    // Real `stereotools=mlev=1:slev=1.3` needs M/S decoding,
                    // which would require a worklet. For v1 we just pass
                    // through — stereo widening is cosmetic enough that its
                    // absence is a < 1 % perceived loss vs FFmpeg.
                    opsSkipped.push('stereotools (passthrough in fallback)');
                    continue;
                }
                case 'afftdn':
                case 'dynaudnorm':
                case 'agate': {
                    opsSkipped.push(f.name + ' (no native equivalent)');
                    continue;
                }
                default: {
                    opsSkipped.push(f.name + ' (unknown to fallback)');
                    continue;
                }
            }
            if (node) nodes.push(node);
        }

        // Connect them serially. Each node may be a real AudioNode or a
        // synthetic { _input, _output, connect } pair (acompressor with
        // makeup, loudnorm). We connect prev._output (or prev) to
        // current._input (or current).
        for (var j = 0; j < nodes.length - 1; j++) {
            var prev = nodes[j];
            var cur = nodes[j + 1];
            var src = (prev && prev._output) ? prev._output : prev;
            var dst = (cur && cur._input) ? cur._input : cur;
            try { src.connect(dst); } catch (e) { /* node mismatch, skip */ }
        }

        var firstNode = nodes.length ? (nodes[0]._input || nodes[0]) : null;
        var lastNode  = nodes.length ? (nodes[nodes.length - 1]._output || nodes[nodes.length - 1]) : null;
        return { firstNode: firstNode, lastNode: lastNode,
            opsApplied: opsApplied, opsSkipped: opsSkipped };
    }

    function _ae(action, payload) {
        try {
            if (typeof window.__ae === 'function') {
                window.__ae('feature_use', Object.assign({ action: action }, payload || {}));
            }
        } catch (_) {}
    }

    var _api = {
        version: '1.0.0',

        canHandleFormat: function(ext) {
            return SUPPORTED_FORMATS.indexOf((ext || '').toLowerCase()) !== -1;
        },

        /**
         * Process a file via Web Audio node graph. No FFmpeg, no WASM.
         *
         * @param {File|Blob} file
         * @param {string}    filterChain  FFmpeg `-af` chain string
         * @param {Object}    opts
         *   ext: 'wav' | 'mp3' (default: 'wav')
         *   slug: tool slug for telemetry
         *   onProgress: (ratio, label) => void  (ratio in [0,1])
         * @returns {Promise<{ blob, name, url, size, opsApplied, opsSkipped }>}
         */
        process: async function(file, filterChain, opts) {
            opts = opts || {};
            var ext = (opts.ext || 'wav').toLowerCase();
            var slug = opts.slug || 'unknown';
            var onProgress = opts.onProgress || function(){};
            // Cancellation via AbortSignal — see AudioStreamPipeline for the
            // pattern. OfflineAudioContext doesn't expose mid-render abort, so
            // we race the render promise against an abort promise. On trip
            // the user sees the cancel immediately; the underlying OAC keeps
            // running until GC (no persistent side effects).
            var signal = opts.signal || null;
            function _abortIfSignaled() {
                if (signal && signal.aborted) {
                    throw new DOMException(
                        (typeof signal.reason === 'string' && signal.reason) || 'WebAudioFallback aborted',
                        'AbortError'
                    );
                }
            }
            if (!this.canHandleFormat(ext)) {
                throw new Error('WebAudioFallback: format "' + ext + '" not supported (only wav/mp3)');
            }
            _abortIfSignaled();
            // 1. Decode input via AudioContext.decodeAudioData
            onProgress(0.05, 'decode');
            var audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            var arrayBuf = await file.arrayBuffer();
            var audioBuffer;
            try {
                audioBuffer = await audioCtx.decodeAudioData(arrayBuf);
            } catch (e) {
                try { audioCtx.close(); } catch (_) {}
                throw new Error('WebAudioFallback: decode failed — ' + ((e && e.message) || e));
            }
            try { audioCtx.close(); } catch (_) {}
            onProgress(0.2, 'decode');

            // 2. Parse filter chain + build graph in OfflineAudioContext
            var filters = splitChain(filterChain || '').map(parseFilter);
            // acompressor / alimiter / loudnorm становятся DynamicsCompressorNode,
            // и каждый держит упреждение 6 мс — последовательно они складываются.
            // При длине = исходнику файл начинался с тишины и терял хвост: рендер
            // длиннее на замеренную задержку, голова отрезается (2026-10-09).
            var nComp = filters.filter(function (f) { return f && /^(acompressor|alimiter|loudnorm)$/.test(f.name); }).length;
            var lead = (nComp && window.NodeLatency) ? nComp * (await window.NodeLatency.compressor(audioBuffer.sampleRate)) : 0;
            var oac = new OfflineAudioContext({
                numberOfChannels: audioBuffer.numberOfChannels,
                length: audioBuffer.length + lead,
                sampleRate: audioBuffer.sampleRate
            });
            var source = oac.createBufferSource();
            source.buffer = audioBuffer;

            var graph = buildGraph(oac, filters);
            if (graph.firstNode && graph.lastNode) {
                source.connect(graph.firstNode);
                graph.lastNode.connect(oac.destination);
            } else {
                // No mappable filters — passthrough
                source.connect(oac.destination);
            }
            source.start();

            // 3. Render. OfflineAudioContext rendering doesn't provide a
            // streaming progress callback, so we report one tick at start
            // and one at completion. Caller can show indeterminate spinner
            // during the gap.
            onProgress(0.25, 'process');
            _abortIfSignaled();
            var rendered;
            try {
                if (signal) {
                    var abortPromise = new Promise(function (_, reject) {
                        if (signal.aborted) {
                            reject(new DOMException(
                                (typeof signal.reason === 'string' && signal.reason) || 'WebAudioFallback aborted',
                                'AbortError'
                            ));
                            return;
                        }
                        signal.addEventListener('abort', function () {
                            reject(new DOMException(
                                (typeof signal.reason === 'string' && signal.reason) || 'WebAudioFallback aborted',
                                'AbortError'
                            ));
                        }, { once: true });
                    });
                    rendered = await Promise.race([oac.startRendering(), abortPromise]);
                } else {
                    rendered = await oac.startRendering();
                }
            } catch (e) {
                if (e && e.name === 'AbortError') throw e;
                throw new Error('WebAudioFallback: render failed — ' + ((e && e.message) || e));
            }
            if (lead) rendered = window.NodeLatency.trimHead(rendered, lead, audioBuffer.length);
            _abortIfSignaled();
            onProgress(0.85, 'encode');

            // 4. Encode to chosen format
            var blob;
            if (ext === 'wav') {
                if (!window.AudioPipeline || !window.AudioPipeline.audioBufferToWav) {
                    if (typeof window.ensureAudioPipeline === 'function') {
                        await window.ensureAudioPipeline();
                    }
                }
                if (!window.AudioPipeline || !window.AudioPipeline.audioBufferToWav) {
                    throw new Error('WebAudioFallback: AudioPipeline (WAV encoder) unavailable');
                }
                blob = window.AudioPipeline.audioBufferToWav(rendered);
            } else if (ext === 'mp3') {
                if (typeof window.ensureAudioPipeline === 'function') {
                    await window.ensureAudioPipeline();
                }
                if (!window.AudioPipeline || typeof window.AudioPipeline.encodeMP3 !== 'function') {
                    throw new Error('WebAudioFallback: AudioPipeline.encodeMP3 unavailable (lamejs missing)');
                }
                try {
                    /* ⚠️ Битрейт — ТОТ, ЧТО ВЫБРАЛ ЧЕЛОВЕК, а не наши 192.
                     *
                     * Этот путь включается на машинах без WASM SIMD, то есть
                     * человек о подмене движка не знает — он знает только, что
                     * выбрал 320 (или «как в источнике»). Молча отдать 192
                     * значит понизить качество без его ведома, а это ровно то,
                     * что у нас запрещено (feedback_no_silent_quality_downgrade).
                     * 192 остаётся лишь как значение, когда звать было нечем. */
                    blob = await window.AudioPipeline.encodeMP3(rendered, { bitrate: opts.bitrate || 192 });
                } catch (encErr) {
                    // lamejs worker holds the full PCM buffer twice (input copy + own
                    // workspace). On low-memory devices that's the OOM trigger we hit
                    // in production (telemetry 2026-05-17..20: 3 sessions, all dm<=2
                    // Chrome, files 1-10 MB MP3 in / MP3 out). Degrade to WAV
                    // gracefully — user gets a larger but valid file instead of a
                    // misleading "Processor doesn't support modern WebAssembly" toast.
                    // Match only the lamejs allocation failure; real bugs still throw.
                    var em = (encErr && encErr.message) || '';
                    var isAlloc = /Array buffer allocation failed|out of memory|Cannot allocate|allocation failed|RangeError/i.test(em);
                    if (!isAlloc) throw encErr;
                    _ae('webaudio_fallback_mp3_oom_degraded_to_wav', {
                        slug: slug, fileMB: Math.round(file.size / 1048576),
                        dm: navigator.deviceMemory || 0, err: em.substring(0, 120)
                    });
                    if (!window.AudioPipeline || !window.AudioPipeline.audioBufferToWav) {
                        if (typeof window.ensureAudioPipeline === 'function') {
                            await window.ensureAudioPipeline();
                        }
                    }
                    if (!window.AudioPipeline || !window.AudioPipeline.audioBufferToWav) {
                        throw encErr;
                    }
                    blob = window.AudioPipeline.audioBufferToWav(rendered);
                    ext = 'wav'; // outName below picks up the new ext
                }
            }
            onProgress(1.0, 'done');

            var outName = 'enhanced.' + ext;
            var url = URL.createObjectURL(blob);
            _ae('webaudio_fallback_used', {
                slug: slug,
                opsApplied: graph.opsApplied.length,
                opsSkipped: graph.opsSkipped.length,
                ext: ext,
                fileMB: Math.round(file.size / 1048576),
                outMB: Math.round(blob.size / 1048576)
            });
            return { blob: blob, name: outName, url: url, size: blob.size,
                opsApplied: graph.opsApplied, opsSkipped: graph.opsSkipped };
        },

        /** Test hook — exposes parser internals so unit tests can assert. */
        _internal: { parseFilter: parseFilter, splitChain: splitChain }
    };

    window.WebAudioFallback = _api;
})();
